import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import PageTitle from '../../components/PageTitle';
import { LoadingButton } from '@mui/lab';
import { FormProvider, RHFTextField } from '../../common/hook-form';
import { useForm } from 'react-hook-form';
import { MerchantFilterCached } from '../../common/ProshipFormComponents';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { apiConstants, getGeneric, postGeneric } from '../../common/constants';
import { useSnackbar } from 'notistack';
import Loader from '../../common/Loader';
import SmsIcon from '@mui/icons-material/Sms';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';


const numberValidation = Yup.string()
    .transform((value) => (value?.trim() === '' ? undefined : value))
    .nullable()
    .test('is-valid-number', 'Please enter a valid non-negative number', (value) => {
        if (!value) return true;
        const num = Number(value);
        return !isNaN(num) && num >= 0;
    });

function PriceField({ label, name, disabled }) {
    return (
        <RHFTextField
            label={label}
            name={name}
            disabled={disabled}
            size="small"
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {'\u20B9'}
                        </Typography>
                    </InputAdornment>
                ),
            }}
        />
    );
}

function SectionCard({ icon, title, subtitle, color, badge, children }) {
    return (
        <Paper
            elevation={1}
            sx={{
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                },
            }}
        >
            <Box sx={{ p: 2.5, pb: 0 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
                    <Avatar
                        sx={{
                            bgcolor: `${color}.light`,
                            color: `${color}.main`,
                            width: 44,
                            height: 44,
                        }}
                    >
                        {icon}
                    </Avatar>
                    <Box flex={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {title}
                            </Typography>
                            {badge && (
                                <Chip
                                    label={badge}
                                    size="small"
                                    color={color}
                                    variant="outlined"
                                    sx={{ fontWeight: 500, height: 22, '& .MuiChip-label': { px: 1 } }}
                                />
                            )}
                        </Stack>
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>
                <Divider />
            </Box>
            <Box sx={{ p: 2.5 }}>{children}</Box>
        </Paper>
    );
}

export default function SMSWAInvoicing() {
    const [isLoading, setIsLoading] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const validationSchema = Yup.object().shape({
        merchant: Yup.string().required('Please select Merchant'),
        perSMS: numberValidation,
        perWA: numberValidation,
        perChannelOrderConfirmationWA: numberValidation,
        perChannelAddressVerificationWA: numberValidation,
        perAWBNdr: numberValidation,
        perFailedDeliveryNdr: numberValidation,
        perResolvedNdr: numberValidation,
        perAWBCall: numberValidation,
        perConnectedCall: numberValidation,
        perResolvedCall: numberValidation,
        perAWBNprCall: numberValidation,
        perConnectedNprCall: numberValidation,
        perResolvedNprCall: numberValidation,
    });

    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            merchant: '',
            perSMS: '',
            perWA: '',
            perAWBNdr: '',
            perFailedDeliveryNdr: '',
            perResolvedNdr: '',
            perAWBCall: '',
            perConnectedCall: '',
            perResolvedCall: '',
            perAWBNprCall: '',
            perConnectedNprCall: '',
            perResolvedNprCall: '',
            perChannelOrderConfirmationWA: '',
            perChannelAddressVerificationWA: '',
            perSMSReturn: '',
            perWAReturn: '',
        },
    });

    const { handleSubmit, watch, reset, setValue } = methods;

    const selectedMerchant = watch('merchant');
    const perAWBNdr = watch('perAWBNdr');
    const perFailedDeliveryNdr = watch('perFailedDeliveryNdr');
    const perResolvedNdr = watch('perResolvedNdr');
    const perAWBCall = watch('perAWBCall');
    const perConnectedCall = watch('perConnectedCall');
    const perResolvedCall = watch('perResolvedCall');
    const perAWBNprCall = watch('perAWBNprCall');
    const perConnectedNprCall = watch('perConnectedNprCall');
    const perResolvedNprCall = watch('perResolvedNprCall');

    const onSubmit = async (data) => {
        setIsLoading(true);
        const payload = {
            perSMS: Number(data.perSMS),
            perWA: Number(data.perWA),
            perChannelOrderConfirmationWA: Number(data.perChannelOrderConfirmationWA),
            perChannelAddressVerificationWA: Number(data.perChannelAddressVerificationWA),
            perAWBNdr: Number(data.perAWBNdr),
            perFailedDeliveryNdr: Number(data.perFailedDeliveryNdr),
            perResolvedNdr: Number(data.perResolvedNdr),
            perAWBCall: Number(data.perAWBCall),
            perConnectedCall: Number(data.perConnectedCall),
            perResolvedCall: Number(data.perResolvedCall),
            perAWBNprCall: Number(data.perAWBNprCall),
            perConnectedNprCall: Number(data.perConnectedNprCall),
            perResolvedNprCall: Number(data.perResolvedNprCall),
            perReturnOtpWA: Number(data.perWAReturn),
            perReturnOtpSMS: Number(data.perSMSReturn),
            perReturnInitiateWA: Number(data.perWAReturn),
            perReturnInitiateSMS: Number(data.perSMSReturn),
            perReturnPlacedWA: Number(data.perWAReturn),
            perReturnPlacedSMS: Number(data.perSMSReturn),
            perReturnAcceptedWA: Number(data.perWAReturn),
            perReturnAcceptedSMS: Number(data.perSMSReturn),
            perReturnRejectedWA: Number(data.perWAReturn),
            perReturnRejectedSMS: Number(data.perSMSReturn),
        };
        try {
            const res = await postGeneric(`${apiConstants.MERCHANT_CONFIG}/${data.merchant}/pricing/communication`, payload);
            reset();
            if (res.status === 200) {
                enqueueSnackbar('Success', { variant: 'success' });
            } else {
                enqueueSnackbar('Some error occurred', { variant: 'error' });
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to submit. Please try again later.', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedMerchant) {
            setLoading(true);
            getGeneric(`${apiConstants.MERCHANT_DETAILS}/${selectedMerchant}`)
                .then((res) => {
                    setValue('perSMS', '');
                    setValue('perWA', '');
                    setValue('perAWBNdr', '');
                    setValue('perFailedDeliveryNdr', '');
                    setValue('perResolvedNdr', '');
                    setValue('perAWBCall', '');
                    setValue('perConnectedCall', '');
                    setValue('perResolvedCall', '');
                    setValue('perAWBNprCall', '');
                    setValue('perConnectedNprCall', '');
                    setValue('perResolvedNprCall', '');
                    setValue('perChannelOrderConfirmationWA', '');
                    setValue('perChannelAddressVerificationWA', '');
                    setValue('perSMSReturn', '');
                    setValue('perWAReturn', '');
                    if (res?.data?.communicationPricing) {
                        const comm = res.data.communicationPricing;
                        const toStr = (v) => (v == null ? '' : String(v));
                        setValue('perSMS', toStr(comm.perSMS));
                        setValue('perWA', toStr(comm.perWA));
                        setValue('perAWBNdr', toStr(comm.perAWBNdr));
                        setValue('perFailedDeliveryNdr', toStr(comm.perFailedDeliveryNdr));
                        setValue('perResolvedNdr', toStr(comm.perResolvedNdr));
                        setValue('perAWBCall', toStr(comm.perAWBCall));
                        setValue('perConnectedCall', toStr(comm.perConnectedCall));
                        setValue('perResolvedCall', toStr(comm.perResolvedCall));
                        setValue('perAWBNprCall', toStr(comm.perAWBNprCall));
                        setValue('perConnectedNprCall', toStr(comm.perConnectedNprCall));
                        setValue('perResolvedNprCall', toStr(comm.perResolvedNprCall));
                        setValue('perChannelOrderConfirmationWA', toStr(comm.perChannelOrderConfirmationWA));
                        setValue('perChannelAddressVerificationWA', toStr(comm.perChannelAddressVerificationWA));
                        setValue('perSMSReturn', toStr(comm.perReturnOtpSMS));
                        setValue('perWAReturn', toStr(comm.perReturnOtpWA));
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    setLoading(false);
                    console.error(err);
                    enqueueSnackbar('Failed to fetch merchant details', { variant: 'error' });
                });
        }
    }, [selectedMerchant]);

    return (
        <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
            {loading && <Loader />}
            <PageTitle>VAS Invoicing</PageTitle>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                {/* Merchant Selector Card */}
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Communication Pricing Configuration
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                            Configure SMS, WhatsApp & calling rates for the selected merchant
                        </Typography>
                        <Grid container>
                            <Grid item xs={12} md={4}>
                                <MerchantFilterCached />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Grid container spacing={3}>
                    {/* Seller Notification */}
                    <Grid item xs={12} md={6}>
                        <SectionCard
                            icon={<SmsIcon />}
                            title="Seller Notification"
                            subtitle="Per notification pricing for seller updates"
                            color="primary"
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <PriceField label="Per SMS" name="perSMS" />
                                </Grid>
                                <Grid item xs={6}>
                                    <PriceField label="Per WhatsApp" name="perWA" />
                                </Grid>
                            </Grid>
                        </SectionCard>
                    </Grid>

                    {/* Returns Notification */}
                    <Grid item xs={12} md={6}>
                        <SectionCard
                            icon={<AssignmentReturnIcon />}
                            title="Returns Notification"
                            subtitle="Per notification pricing for return updates"
                            color="warning"
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <PriceField label="Per SMS" name="perSMSReturn" />
                                </Grid>
                                <Grid item xs={6}>
                                    <PriceField label="Per WhatsApp" name="perWAReturn" />
                                </Grid>
                            </Grid>
                        </SectionCard>
                    </Grid>

                    {/* Channel Order Verification */}
                    <Grid item xs={12}>
                        <SectionCard
                            icon={<VerifiedUserIcon />}
                            title="Channel Order Verification"
                            subtitle="WhatsApp pricing for order confirmation & address verification"
                            color="success"
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <PriceField label="Order Confirmation (WA)" name="perChannelOrderConfirmationWA" />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <PriceField label="Address Verification (WA)" name="perChannelAddressVerificationWA" />
                                </Grid>
                            </Grid>
                        </SectionCard>
                    </Grid>

                    {/* Select 1 Alert */}
                    <Grid item xs={12}>
                        <Alert severity="error">
                            Only one pricing model can be active per section below. Enter a value in one field to lock the others.
                        </Alert>
                    </Grid>

                    {/* NDR WhatsApp */}
                    <Grid item xs={12}>
                        <SectionCard
                            icon={<WhatsAppIcon />}
                            title="NDR WhatsApp"
                            subtitle="Filling one field will disable the others"
                            color="success"
                            badge="Select 1"
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <PriceField
                                        label="Per AWB NDR"
                                        name="perAWBNdr"
                                        disabled={!!perFailedDeliveryNdr || !!perResolvedNdr}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <PriceField
                                        label="Per Failed Delivery NDR"
                                        name="perFailedDeliveryNdr"
                                        disabled={!!perAWBNdr || !!perResolvedNdr}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <PriceField
                                        label="Per Resolved NDR"
                                        name="perResolvedNdr"
                                        disabled={!!perAWBNdr || !!perFailedDeliveryNdr}
                                    />
                                </Grid>
                            </Grid>
                        </SectionCard>
                    </Grid>

                    {/* NDR Calling */}
                    <Grid item xs={12}>
                        <SectionCard
                            icon={<PhoneInTalkIcon />}
                            title="NDR Calling"
                            subtitle="Filling one field will disable the others"
                            color="secondary"
                            badge="Select 1"
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <PriceField
                                        label="Per AWB Call"
                                        name="perAWBCall"
                                        disabled={!!perConnectedCall || !!perResolvedCall}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <PriceField
                                        label="Per Connected Call"
                                        name="perConnectedCall"
                                        disabled={!!perAWBCall || !!perResolvedCall}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <PriceField
                                        label="Per Resolved Call"
                                        name="perResolvedCall"
                                        disabled={!!perAWBCall || !!perConnectedCall}
                                    />
                                </Grid>
                            </Grid>
                        </SectionCard>
                    </Grid>

                    {/* NPR Calling */}
                    <Grid item xs={12}>
                        <SectionCard
                            icon={<PhoneInTalkIcon />}
                            title="NPR Calling"
                            subtitle="Filling one field will disable the others"
                            color="error"
                            badge="Select 1"
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <PriceField
                                        label="Per AWB NPR Call"
                                        name="perAWBNprCall"
                                        disabled={!!perConnectedNprCall || !!perResolvedNprCall}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <PriceField
                                        label="Per Connected NPR Call"
                                        name="perConnectedNprCall"
                                        disabled={!!perAWBNprCall || !!perResolvedNprCall}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <PriceField
                                        label="Per Resolved NPR Call"
                                        name="perResolvedNprCall"
                                        disabled={!!perAWBNprCall || !!perConnectedNprCall}
                                    />
                                </Grid>
                            </Grid>
                        </SectionCard>
                    </Grid>
                </Grid>

                {/* Action Buttons */}
                <Stack direction="row" spacing={1} sx={{ mt: 2,ml:1 }}>
                    <LoadingButton
                        loading={isLoading}
                        variant="contained"
                        type="submit"
                        size="medium"
                    >
                        Submit
                    </LoadingButton>
                    <Button
                        variant="contained"
                        color="error"
                        size="medium"
                        onClick={() => reset()}
                    >
                        Reset
                    </Button>
                </Stack>
            </FormProvider>
        </Box>
    );
}
